import { FeatureId } from '@backModels/FeatureId';
import { RouteSymbols } from 'src/app/route-symbols';
import { ITabItem } from 'src/app/models/common/common';

/** The permission checks campaign navigation depends on; CurrentUserService satisfies it. */
export interface CampaignAccess {
  isReadAllowedOnEntity(featureId: FeatureId, campaignGuid: string): boolean;
  canWriteCampaignSettings(campaignGuid: string): boolean;
}

/** Settings tabs the user may open on a campaign, in display order. */
export function buildCampaignSettingsTabs(access: CampaignAccess, campaignGuid: string): ITabItem[] {
  const tabs: ITabItem[] = [];

  if (access.isReadAllowedOnEntity(FeatureId.CampaignSettingsGeneralData, campaignGuid)) {
    tabs.push({ route: RouteSymbols.SettingsGeneralData, label: 'GeneralData', icon: 'home' });
  }
  // Campaign settings are an editing surface: read-only roles see the campaign's general data
  // but not the knobs. Same condition CampaignConfigComponent uses to allow saving.
  if (access.canWriteCampaignSettings(campaignGuid)) {
    tabs.push({ route: RouteSymbols.SettingsConfiguration, label: 'Settings', icon: 'tune' });
  }
  if (access.isReadAllowedOnEntity(FeatureId.CampaignSettingsCustomers, campaignGuid)) {
    tabs.push({ route: RouteSymbols.SettingsCustomers, label: 'Customers', icon: 'group' });
  }
  if (access.isReadAllowedOnEntity(FeatureId.CampaignSettingsUsers, campaignGuid)) {
    tabs.push({ route: RouteSymbols.Users, label: 'Users', icon: 'manage_accounts' });
  }
  if (access.isReadAllowedOnEntity(FeatureId.CampaignSettingsStyles, campaignGuid)) {
    tabs.push({ route: RouteSymbols.SettingsStyles, label: 'Styles', icon: 'palette' });
  }
  if (access.isReadAllowedOnEntity(FeatureId.CampaignSettingsEmails, campaignGuid)) {
    tabs.push({ route: RouteSymbols.SettingsEmails, label: 'Emails', icon: 'mail' });
  }
  if (access.isReadAllowedOnEntity(FeatureId.CustomerFeedbacks, campaignGuid)) {
    tabs.push({ route: RouteSymbols.SettingsCustomerFeedbacks, label: 'Feedbacks', icon: 'star_rate' });
  }
  if (access.isReadAllowedOnEntity(FeatureId.CampaignSettingsEnabledZones, campaignGuid)) {
    tabs.push({ route: RouteSymbols.SettingsEnabledZones, label: 'AllowedProvinces', icon: 'map' });
  }
  if (access.isReadAllowedOnEntity(FeatureId.CampaignSettingsHistory, campaignGuid)) {
    tabs.push({ route: RouteSymbols.SettingsHistory, label: 'History', icon: 'history' });
  }

  return tabs;
}

/**
 * Router commands to enter a campaign: the first section the user may open. Checkups first so
 * existing roles keep landing where they always did; roles without checkups (customer registrars)
 * go straight to their first settings tab instead of a 403.
 */
export function resolveCampaignEntryRoute(access: CampaignAccess, campaignGuid: string): string[] {
  const base = ['/campaigns', campaignGuid];
  if (access.isReadAllowedOnEntity(FeatureId.Checkups, campaignGuid)) return [...base, RouteSymbols.CheckupsAll];
  if (access.isReadAllowedOnEntity(FeatureId.Dashboard, campaignGuid)) return [...base, RouteSymbols.Dashboard];
  const firstTab = buildCampaignSettingsTabs(access, campaignGuid)[0]?.route;
  if (firstTab) return [...base, RouteSymbols.Settings, firstTab];
  return [...base, RouteSymbols.CheckupsAll];
}
